import { useState } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PromoService, { PromoCode } from "@/app/_services/PromoService";
import { promoSchema } from "./PromoSchema";
import { toast } from "@/app/_utils/toast";

type FormData = yup.InferType<typeof promoSchema>;

const EMPTY_VALUES: FormData = {
  code: "",
  discount_percent: 10,
  all_shows: true,
  show_ids: [],
  active: true,
};

export function usePromoFormFlow({
  defaultPromo,
  onSaved,
}: {
  defaultPromo?: PromoCode | null;
  onSaved: () => void;
}) {
  const [isSaving, setIsSaving] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(promoSchema),
    values: defaultPromo
      ? {
          code: defaultPromo.code,
          discount_percent: defaultPromo.discount_percent,
          all_shows: (defaultPromo.shows?.length ?? 0) === 0,
          show_ids: defaultPromo.shows ?? [],
          active: defaultPromo.active,
        }
      : EMPTY_VALUES,
  });

  const handleSave = handleSubmit(async (fields: FieldValues) => {
    setIsSaving(true);
    const payload = {
      code: String(fields.code).trim().toUpperCase(),
      discount_percent: Number(fields.discount_percent),
      active: !!fields.active,
      // An empty list is how the backend represents "applies to all shows".
      shows: fields.all_shows ? [] : (fields.show_ids as string[]),
    };
    try {
      if (defaultPromo) {
        await PromoService.updatePromoCodeAsync(defaultPromo.id, payload);
        toast.success("Promo code updated");
      } else {
        await PromoService.createPromoCodeAsync(payload);
        toast.success("Promo code created");
        reset(EMPTY_VALUES);
      }
      onSaved();
    } catch (e: any) {
      toast.error(e?.message || "Failed to save promo code");
    } finally {
      setIsSaving(false);
    }
  });

  return { control, errors, isSaving, handleSave };
}
